import { useState, useCallback, useRef, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Upload, Link, Play, Download, Send, Edit3,
  Film, RefreshCw, X, ChevronLeft, Sparkles, AlertCircle, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  VideoStatus, formatDuration,
  uploadVideo, startDetectMoments, pollJob, segmentsToClips, extractClip,
} from '@/services/youtubeService';
import { driveApi, processingApi } from '@/services/api';
import { VideoEditorPage } from '@/components/VideoEditor';

export default function YouTubePage() {
  const { activeBrand, activeCreator, trialActive } = useApp();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [youtubeLink, setYoutubeLink] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedClip, setSelectedClip] = useState(null);
  const [editingClip, setEditingClip] = useState(null);
  const [downloadingClipId, setDownloadingClipId] = useState(null);
  const abortRefs = useRef({});
  const clipVideoRef = useRef(null);
  const [clipPlaying, setClipPlaying] = useState(false);

  // Compute clip preview URL (short extracted segment — avoids loading full video)
  const BASE = process.env.REACT_APP_BACKEND_URL;
  const clipPreviewUrl = selectedClip
    ? `${BASE}/api/youtube/clip-preview/${selectedClip.videoId}?start=${selectedClip.startTime}&end=${selectedClip.endTime}`
    : null;

  // Reset player state when clip changes
  useEffect(() => {
    setClipPlaying(false);
  }, [selectedClip?.id]);

  // Handle clip download
  const handleDownloadClip = () => {
    if (!clipPreviewUrl) return;
    const a = document.createElement('a');
    a.href = clipPreviewUrl + '&dl=true';
    a.download = `${(selectedClip?.title || 'clip').replace(/\s+/g, '_')}.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const updateProject = useCallback((id, updates) => {
    setProjects(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
    setSelectedProject(prev => prev?.id === id ? { ...prev, ...updates } : prev);
  }, []);

  // ── Load existing projects from DB on mount ────────────────────────────────
  useEffect(() => {
    if (!activeBrand) return;
    const load = async () => {
      setLoading(true);
      try {
        const BASE = process.env.REACT_APP_BACKEND_URL;
        const files = await driveApi.listFiles({ brand_id: activeBrand.id, folder: 'yt_raw' });
        if (!files?.length) return;

        const loaded = await Promise.all(files.map(async (f) => {
          const base = {
            id: f.id,
            name: f.name,
            source: 'upload',
            videoId: f.id,
            videoUrl: f.url || `${BASE}/api/uploads/videos/${f.id}.mp4`,
            thumbnailUrl: f.thumbnail_url || null,
            duration: formatDuration(f.duration_seconds || 0),
            durationSeconds: f.duration_seconds || 0,
            status: VideoStatus.READY,
            uploadProgress: 100,
            detectProgress: 100,
            error: null,
            clips: [],
          };
          // Find completed detect_moments job for this video
          try {
            const jobs = await processingApi.listJobs(f.id);
            const doneJob = (jobs || [])
              .filter(j => j.job_type === 'detect_moments' && j.status === 'done')
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
            if (doneJob?.params?.segments?.length) {
              base.clips = segmentsToClips(doneJob.params.segments, f.id);
            }
          } catch (_) {}
          return base;
        }));

        setProjects(loaded.filter(Boolean));
      } catch (err) {
        console.error('Error loading projects:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeBrand?.id]);

  // ── Upload + Detect ────────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!activeBrand) {
      toast.error('Selecciona una marca primero');
      return;
    }

    // Check available disk space before starting
    try {
      const space = await driveApi.diskSpace();
      {
        if (file.size > space.free_bytes * 0.9) {
          toast.error(
            `Espacio insuficiente en el servidor. Disponible: ${space.free_gb} GB, tu vídeo: ${(file.size / 1024 ** 3).toFixed(1)} GB. Usa un vídeo más pequeño.`,
            { duration: 8000 }
          );
          return;
        }
      }
    } catch (_) { /* silently ignore disk check errors */ }

    const tempId = `yt-${Date.now()}`;
    const project = {
      id: tempId,
      name: file.name,
      source: 'upload',
      videoId: null,
      videoUrl: null,
      thumbnailUrl: null,
      duration: '0:00',
      durationSeconds: 0,
      status: VideoStatus.UPLOADING,
      uploadProgress: 0,
      detectProgress: 0,
      error: null,
      clips: [],
    };
    setProjects(prev => [project, ...prev]);

    const abort = new AbortController();
    abortRefs.current[tempId] = abort;

    try {
      // Phase 1: Upload (chunked — 1MB per chunk to bypass proxy limits)
      toast.info(`Subiendo "${file.name}" en fragmentos...`);

      const uploaded = await uploadVideo(
        file,
        activeBrand.id,
        activeCreator?.id || activeBrand.creator_id || 'unknown',
        (pct) => updateProject(tempId, { uploadProgress: pct }),
        abort.signal,
      );

      if (abort.signal.aborted) return;

      // Update project with real videoId
      const newId = uploaded.id;
      updateProject(tempId, {
        id: newId,
        videoId: newId,
        videoUrl: uploaded.url || null,
        thumbnailUrl: uploaded.thumbnail_url || null,
        duration: formatDuration(uploaded.duration_seconds || 0),
        durationSeconds: uploaded.duration_seconds || 0,
        status: VideoStatus.DETECTING,
        uploadProgress: 100,
      });

      // Re-key in abortRefs
      abortRefs.current[newId] = abort;
      delete abortRefs.current[tempId];

      // Phase 2: Detect viral moments (async job + poll)
      toast.info('Analizando momentos virales con IA...');
      const detectJob = await startDetectMoments(newId, 8);

      if (abort.signal.aborted) return;

      const doneJob = await pollJob(
        detectJob.id,
        (pct) => updateProject(newId, { detectProgress: pct }),
        abort.signal,
      );

      const clips = segmentsToClips(doneJob.params?.segments || [], newId);
      updateProject(newId, { status: VideoStatus.READY, clips });
      toast.success(`"${file.name}" listo — ${clips.length} clips encontrados`);

    } catch (err) {
      if (err.message === 'Cancelled') return;
      const failId = abortRefs.current[tempId] ? tempId : Object.keys(abortRefs.current)[0];
      const pid = projects.find(p => p.name === file.name)?.id || tempId;
      updateProject(pid, { status: VideoStatus.FAILED, error: err.message });
      toast.error(`Error procesando "${file.name}": ${err.message}`);
    } finally {
      delete abortRefs.current[tempId];
    }
  };

  // ── YouTube URL (not yet supported — needs yt-dlp backend) ────────────────
  const handleYoutubeLink = () => {
    toast.info('Importación desde URL de YouTube estará disponible próximamente. Por ahora sube el archivo de video directamente.');
    setYoutubeLink('');
  };

  // ── Retry failed project ───────────────────────────────────────────────────
  const handleRetry = useCallback((project) => {
    toast.info(`Reintentando análisis de "${project.name}"...`);
    updateProject(project.id, { status: VideoStatus.DETECTING, detectProgress: 0, error: null });

    const abort = new AbortController();
    abortRefs.current[project.id] = abort;

    startDetectMoments(project.videoId, 8)
      .then(job => pollJob(job.id, (pct) => updateProject(project.id, { detectProgress: pct }), abort.signal))
      .then(done => {
        const clips = segmentsToClips(done.params?.segments || [], project.videoId);
        updateProject(project.id, { status: VideoStatus.READY, clips });
        toast.success(`${clips.length} clips encontrados`);
      })
      .catch(err => {
        if (err.message === 'Cancelled') return;
        updateProject(project.id, { status: VideoStatus.FAILED, error: err.message });
        toast.error(err.message);
      })
      .finally(() => delete abortRefs.current[project.id]);
  }, [updateProject]);

  // ── Delete project ─────────────────────────────────────────────────────────
  const handleDelete = useCallback((projectId) => {
    abortRefs.current[projectId]?.abort();
    delete abortRefs.current[projectId];
    setProjects(prev => prev.filter(p => p.id !== projectId));
    if (selectedProject?.id === projectId) { setSelectedProject(null); setSelectedClip(null); }
    toast.success('Proyecto eliminado');
  }, [selectedProject]);

  // ── Download clip (extract + poll + download) ──────────────────────────────
  const handleDownload = async (clip) => {
    setDownloadingClipId(clip.id);
    try {
      toast.info(`Extrayendo clip "${clip.title}"...`);
      const job = await extractClip(clip.videoId, clip.startTime, clip.endTime, true);
      const done = await pollJob(job.id, () => {});
      if (done.result_url) {
        const a = document.createElement('a');
        a.href = done.result_url;
        a.download = `${clip.title}.mp4`;
        a.click();
        toast.success('Clip descargado');
      } else {
        toast.error('No se pudo obtener la URL del clip');
      }
    } catch (err) {
      toast.error(`Error extrayendo clip: ${err.message}`);
    } finally {
      setDownloadingClipId(null);
    }
  };

  // ── Status helpers ─────────────────────────────────────────────────────────
  const getStatusColor = (status) => ({
    [VideoStatus.UPLOADING]: 'hsl(var(--primary))',
    [VideoStatus.DETECTING]: 'hsl(var(--accent))',
    [VideoStatus.READY]: 'hsl(var(--success, 145 70% 50%))',
    [VideoStatus.FAILED]: 'hsl(var(--destructive))',
  }[status] || 'hsl(var(--foreground-subtle))');

  const getStatusLabel = (p) => ({
    [VideoStatus.UPLOADING]: `Subiendo ${p.uploadProgress}%`,
    [VideoStatus.DETECTING]: `Analizando con IA${p.detectProgress > 0 ? ` ${p.detectProgress}%` : '...'}`,
    [VideoStatus.READY]: 'Listo',
    [VideoStatus.FAILED]: 'Error',
  }[p.status] || p.status);

  const getProgress = (p) =>
    p.status === VideoStatus.UPLOADING ? p.uploadProgress :
    p.status === VideoStatus.DETECTING ? (50 + (p.detectProgress / 2)) :
    p.status === VideoStatus.READY ? 100 : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2" style={{ color: 'hsl(var(--foreground))' }}>
          YouTube
        </h1>
        <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>
          Extrae clips virales de tus vídeos con IA (Whisper + GPT)
        </p>
        {activeBrand && (
          <p className="text-xs mt-2" style={{ color: 'hsl(var(--primary))' }}>
            Marca: {activeBrand.name}
          </p>
        )}
      </div>

      {/* Upload Section */}
      <div className="mb-8 p-6 rounded-xl" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Upload File */}
          <div>
            <label className="block mb-3">
              <span className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>
                Subir Vídeo
              </span>
            </label>
            <label
              className="flex flex-col items-center justify-center p-8 rounded-lg cursor-pointer transition-colors"
              style={{ border: '2px dashed hsl(var(--border))' }}
              onMouseOver={e => e.currentTarget.style.borderColor = 'hsl(var(--primary))'}
              onMouseOut={e => e.currentTarget.style.borderColor = 'hsl(var(--border))'}
            >
              <input
                type="file"
                accept="video/*"
                className="hidden"
                onChange={handleFileUpload}
                data-testid="youtube-upload-input"
              />
              <Upload size={32} style={{ color: 'hsl(var(--primary))' }} />
              <span className="mt-2 text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>
                Arrastra o haz click para subir
              </span>
              <span className="mt-1 text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                MP4, MOV, AVI, MKV hasta 2 GB
              </span>
            </label>
          </div>

          {/* YouTube Link */}
          <div>
            <label className="block mb-3">
              <span className="text-sm font-medium" style={{ color: 'hsl(var(--foreground))' }}>
                Link de YouTube
              </span>
              <span className="ml-2 text-xs px-2 py-0.5 rounded"
                style={{ background: 'hsl(var(--surface-elevated))', color: 'hsl(var(--foreground-subtle))' }}>
                Próximamente
              </span>
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Link size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'hsl(var(--foreground-subtle))' }} />
                <Input
                  placeholder="https://youtube.com/watch?v=..."
                  value={youtubeLink}
                  onChange={(e) => setYoutubeLink(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleYoutubeLink()}
                  className="pl-10 opacity-60"
                  data-testid="youtube-link-input"
                />
              </div>
              <Button onClick={handleYoutubeLink} variant="outline" data-testid="youtube-import-btn">
                Importar
              </Button>
            </div>
            <p className="mt-2 text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
              La descarga directa de YouTube estará disponible pronto
            </p>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
            Proyectos ({projects.length})
          </h2>
        </div>

        {projects.length === 0 ? (
          <div className="text-center py-16 rounded-xl" style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
            {loading ? (
              <>
                <Loader2 size={40} className="mx-auto mb-3 animate-spin" style={{ color: 'hsl(var(--primary))' }} />
                <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>Cargando proyectos...</p>
              </>
            ) : (
              <>
                <Film size={48} className="mx-auto mb-4" style={{ color: 'hsl(var(--foreground-subtle))' }} />
                <p className="text-sm" style={{ color: 'hsl(var(--foreground-muted))' }}>Sin proyectos aún</p>
                <p className="text-xs mt-1" style={{ color: 'hsl(var(--foreground-subtle))' }}>Sube un vídeo para comenzar</p>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {projects.map(project => (
              <div
                key={project.id}
                className="group rounded-xl overflow-hidden cursor-pointer transition-transform hover:scale-[1.02]"
                style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}
                onClick={() => project.status === VideoStatus.READY && setSelectedProject(project)}
                data-testid={`project-${project.id}`}
              >
                {/* Thumbnail */}
                <div className="aspect-video relative" style={{ background: 'hsl(var(--surface-elevated))' }}>
                  {project.thumbnailUrl ? (
                    <img src={project.thumbnailUrl} alt={project.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {project.status === VideoStatus.DETECTING ? (
                        <Loader2 size={32} className="animate-spin" style={{ color: 'hsl(var(--accent))' }} />
                      ) : (
                        <Film size={32} style={{ color: 'hsl(var(--foreground-subtle))' }} />
                      )}
                    </div>
                  )}

                  {/* Duration badge */}
                  {project.status === VideoStatus.READY && (
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded text-xs font-medium"
                      style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }}>
                      {project.duration}
                    </div>
                  )}

                  {/* Status badge */}
                  <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium"
                    style={{ background: getStatusColor(project.status), color: '#fff' }}>
                    {getStatusLabel(project)}
                  </div>

                  {/* Progress bar */}
                  {project.status !== VideoStatus.READY && project.status !== VideoStatus.FAILED && (
                    <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: 'rgba(0,0,0,0.3)' }}>
                      <div
                        className="h-full transition-all duration-300"
                        style={{ width: `${getProgress(project)}%`, background: 'hsl(var(--primary))' }}
                      />
                    </div>
                  )}

                  {/* Hover overlay */}
                  {project.status === VideoStatus.READY && (
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Play size={40} className="text-white" />
                    </div>
                  )}

                  {/* Delete button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(project.id); }}
                    className="absolute top-2 right-2 p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: 'rgba(0,0,0,0.6)' }}
                    data-testid={`delete-project-${project.id}`}
                  >
                    <X size={14} className="text-white" />
                  </button>
                </div>

                {/* Info */}
                <div className="p-3">
                  <p className="text-sm font-medium truncate" style={{ color: 'hsl(var(--foreground))' }}>
                    {project.name}
                  </p>
                  {project.status === VideoStatus.READY && (
                    <p className="text-xs mt-1" style={{ color: 'hsl(var(--primary))' }}>
                      {project.clips.length} clips detectados
                    </p>
                  )}
                  {project.status === VideoStatus.DETECTING && (
                    <p className="text-xs mt-1" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                      Whisper + GPT analizando...
                    </p>
                  )}
                  {project.status === VideoStatus.FAILED && (
                    <Button
                      size="sm" variant="destructive"
                      className="mt-2 w-full h-7 text-xs"
                      onClick={(e) => { e.stopPropagation(); handleRetry(project); }}
                    >
                      <RefreshCw size={12} className="mr-1" />
                      Reintentar
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={() => { setSelectedProject(null); setSelectedClip(null); }}
        >
          <div
            className="w-full max-w-6xl max-h-[90vh] flex flex-col rounded-xl overflow-hidden"
            style={{ background: 'hsl(var(--background))' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0"
              style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="flex items-center gap-3">
                <button onClick={() => { setSelectedProject(null); setSelectedClip(null); }}
                  className="p-1 rounded hover:bg-surface-hover">
                  <ChevronLeft size={20} style={{ color: 'hsl(var(--foreground-muted))' }} />
                </button>
                <div>
                  <h2 className="text-lg font-bold" style={{ color: 'hsl(var(--foreground))' }}>
                    {selectedProject.name}
                  </h2>
                  <p className="text-xs" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                    {selectedProject.duration} · {selectedProject.clips.length} clips
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setSelectedProject(null); setSelectedClip(null); }}>
                <X size={18} />
              </Button>
            </div>

            {/* Content */}
            <div className="flex flex-1 overflow-hidden min-h-0">
              {/* Clips Grid */}
              <div className="flex-1 overflow-y-auto p-6">
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'hsl(var(--foreground))' }}>
                  Clips Detectados por IA
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {selectedProject.clips.map((clip, index) => (
                    <div
                      key={clip.id}
                      className={`rounded-xl overflow-hidden cursor-pointer transition-all ${selectedClip?.id === clip.id ? 'ring-2' : ''}`}
                      style={{
                        background: 'hsl(var(--surface))',
                        border: '1px solid hsl(var(--border))',
                        '--tw-ring-color': 'hsl(var(--primary))',
                      }}
                      onClick={() => setSelectedClip(clip)}
                      data-testid={`clip-${clip.id}`}
                    >
                      <div className="aspect-[9/16] relative" style={{ background: 'hsl(var(--surface-elevated))' }}>
                        {clip.thumbnailUrl ? (
                          <img
                            src={clip.thumbnailUrl}
                            alt={clip.title}
                            className="w-full h-full object-cover"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Play size={24} style={{ color: 'hsl(var(--foreground-subtle))' }} />
                          </div>
                        )}
                        <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded text-xs"
                          style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }}>
                          {formatDuration(clip.duration)}
                        </div>
                        <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1"
                          style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}>
                          <Sparkles size={10} />
                          {clip.viralScore}
                        </div>
                        {trialActive && (
                          <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-xs"
                            style={{ background: 'hsl(var(--warning))', color: '#000' }}>
                            WM
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="text-sm font-medium truncate" style={{ color: 'hsl(var(--foreground))' }}>
                          {clip.title}
                        </p>
                        <p className="text-xs mt-1" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                          {formatDuration(clip.startTime)} → {formatDuration(clip.endTime)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right panel — clip player */}
              {selectedClip ? (
                <div className="w-80 border-l flex-shrink-0 flex flex-col"
                  style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface))' }}>
                  {/* Video player */}
                  <div className="relative flex-shrink-0 mx-4 mt-4 rounded-xl overflow-hidden"
                    style={{ background: '#000', aspectRatio: '9/16', maxHeight: '260px' }}>
                    <>
                      <video
                        ref={clipVideoRef}
                        key={clipPreviewUrl}
                        src={clipPreviewUrl}
                        className="w-full h-full object-contain"
                        playsInline
                        preload="auto"
                        onPlay={() => setClipPlaying(true)}
                        onPause={() => setClipPlaying(false)}
                        onEnded={() => { setClipPlaying(false); if (clipVideoRef.current) clipVideoRef.current.currentTime = 0; }}
                      />
                      {/* Play/pause overlay */}
                      <button
                        className="absolute inset-0 flex items-center justify-center transition-opacity"
                        style={{ opacity: clipPlaying ? 0 : 1, background: 'rgba(0,0,0,0.3)' }}
                        onClick={() => {
                          const v = clipVideoRef.current;
                          if (!v) return;
                          if (v.paused) { v.play().catch(() => {}); } else { v.pause(); }
                        }}
                      >
                        {clipPlaying ? null : (
                          <div className="w-14 h-14 rounded-full flex items-center justify-center"
                            style={{ background: 'hsl(var(--primary) / 0.9)' }}>
                            <Play size={28} className="ml-1 text-black" />
                          </div>
                        )}
                      </button>
                    </>
                    {/* Score badge */}
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1"
                      style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}>
                      <Sparkles size={10} />
                      {selectedClip.viralScore}
                    </div>
                  </div>

                  {/* Clip info */}
                  <div className="px-4 pt-3 pb-2">
                    <p className="text-sm font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
                      {selectedClip.title}
                    </p>
                    <p className="text-xs mt-1" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                      {formatDuration(selectedClip.duration)} · Score: {selectedClip.viralScore}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                      {formatDuration(selectedClip.startTime)} — {formatDuration(selectedClip.endTime)}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="px-4 pb-4 flex flex-col gap-2 mt-auto">
                    <Button size="sm" className="w-full justify-start"
                      onClick={() => setEditingClip({
                        ...selectedClip,
                        videoUrl: clipPreviewUrl,
                        startTime: 0,
                        endTime: selectedClip.duration,
                        originalStartTime: selectedClip.startTime,
                      })}
                      data-testid="edit-clip-btn">
                      <Edit3 size={14} className="mr-2" />
                      Editar
                    </Button>
                    <Button size="sm" variant="outline" className="w-full justify-start">
                      <Send size={14} className="mr-2" />
                      Enviar a Reels
                    </Button>
                    <Button
                      size="sm" variant="outline" className="w-full justify-start"
                      disabled={!clipPreviewUrl}
                      onClick={handleDownloadClip}
                      data-testid="download-clip-btn">
                      <Download size={14} className="mr-2" />
                      Descargar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="w-56 border-l flex-shrink-0 flex items-center justify-center"
                  style={{ borderColor: 'hsl(var(--border))' }}>
                  <p className="text-xs text-center px-4" style={{ color: 'hsl(var(--foreground-subtle))' }}>
                    Selecciona un clip para ver las opciones
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Video Editor */}
      {editingClip && (
        <VideoEditorPage
          video={{
            id: editingClip.videoId,
            title: editingClip.title,
            name: editingClip.title,
            url: editingClip.videoUrl,
            durationSeconds: editingClip.duration,
            startTime: editingClip.startTime,
            endTime: editingClip.endTime,
            originalStartTime: editingClip.originalStartTime || 0,
          }}
          moduleType="youtube"
          onSave={(updatedVideo) => {
            if (selectedProject) {
              const updatedClips = selectedProject.clips.map(c =>
                c.id === editingClip.id
                  ? { ...c, title: updatedVideo.settings?.title || c.title }
                  : c
              );
              updateProject(selectedProject.id, { clips: updatedClips });
            }
          }}
          onClose={() => setEditingClip(null)}
          onExport={(video) => {
            toast.success(`Exportando "${video.settings?.title}"...`);
            setEditingClip(null);
          }}
        />
      )}

      {/* Processing warning */}
      {projects.some(p => p.status === VideoStatus.DETECTING || p.status === VideoStatus.UPLOADING) && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full flex items-center gap-2"
          style={{ background: 'hsl(var(--warning))', color: '#000' }}>
          <AlertCircle size={16} />
          <span className="text-sm font-medium">
            Mantén esta pestaña abierta mientras se procesa el vídeo
          </span>
        </div>
      )}
    </div>
  );
}
